import GoogleCodeField, { toGoogleCode } from '@/components/GoogleCodeField';
import { changePassword } from '@/services/api/User';
import { removeLoginInfo, removeTokens } from '@/store';
import { handleError, useTranslation } from '@/utils/util';
import { LockOutlined } from '@ant-design/icons';
import { ModalForm, ProFormText } from '@ant-design/pro-components';
import { history } from '@umijs/max';
import { Form, message } from 'antd';
import React from 'react';

interface ChangePasswordModalProps {
  open: boolean;
  close: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, close }) => {
  const t = useTranslation();
  const [form] = Form.useForm();

  const handleSubmit = async (values: any) => {
    try {
      // 动态口令随接口一起提交，由后端校验
      const res = await changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
        googleCode: toGoogleCode(values.googleCode),
      });
      if (res?.code === '200') {
        message.success(t('common.changePasswordSuccess'));
        // 修改密码后原登录态失效，清除本地登录信息并退出到登录页
        removeTokens('');
        removeLoginInfo();
        history.replace({ pathname: '/user/login' });
        return true;
      }
    } catch (err) {
      handleError(err);
    }
    return false;
  };

  return (
    <ModalForm
      title={t('common.changePassword')}
      form={form}
      open={open}
      width={520}
      layout="vertical"
      onFinish={handleSubmit}
      onOpenChange={(visible) => {
        if (!visible) {
          // 关闭时清空上一次的输入，避免重开残留
          form.resetFields();
          close();
        }
      }}
      modalProps={{
        centered: true,
        destroyOnHidden: true,
        maskClosable: false,
        onCancel: close,
      }}
      submitter={{
        searchConfig: {
          submitText: t('common.confirm'),
          resetText: t('common.cancel'),
        },
      }}
    >
      <ProFormText.Password
        name="oldPassword"
        label={t('common.oldPassword')}
        placeholder={t('common.pleaseEnterOldPassword')}
        // autoComplete="new-password" 阻止浏览器自动填充已保存的密码
        fieldProps={{ prefix: <LockOutlined />, autoComplete: 'new-password' }}
        rules={[
          { required: true, message: t('common.pleaseEnterOldPassword') },
          { min: 6, message: t('common.passwordMin6') },
        ]}
      />
      <ProFormText.Password
        name="newPassword"
        label={t('common.newPassword')}
        placeholder={t('common.pleaseEnterNewPassword')}
        fieldProps={{
          prefix: <LockOutlined />,
          autoComplete: 'new-password',
          maxLength: 50,
        }}
        rules={[
          { required: true, message: t('common.pleaseEnterNewPassword') },
          { min: 6, max: 50, message: t('common.passwordLength6to50') },
          // 仅允许英文、数字、特殊字符（ASCII 可见字符，排除空格与中文）
          {
            pattern: /^[\x21-\x7e]+$/,
            message: t('common.passwordFormat'),
          },
        ]}
      />
      <ProFormText.Password
        name="confirmPassword"
        label={t('common.confirmPassword')}
        placeholder={t('common.pleaseEnterNewPasswordAgain')}
        dependencies={['newPassword']}
        fieldProps={{
          prefix: <LockOutlined />,
          autoComplete: 'new-password',
          maxLength: 50,
        }}
        rules={[
          { required: true, message: t('common.pleaseEnterNewPasswordAgain') },
          { min: 6, max: 50, message: t('common.passwordLength6to50') },
          // 仅允许英文、数字、特殊字符（ASCII 可见字符，排除空格与中文）
          {
            pattern: /^[\x21-\x7e]+$/,
            message: t('common.passwordFormat'),
          },
          // 与新密码保持一致
          ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
              if (!value || getFieldValue('newPassword') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t('common.passwordNotMatch')));
            },
          }),
        ]}
      />
      <GoogleCodeField />
    </ModalForm>
  );
};

export default ChangePasswordModal;
